"use client";
import { useRef, useEffect } from "react";
import gsap from "gsap";
import styles from "./Cases.module.scss";
import { TuxdiArrow } from "./TuxdiArrow";
import LinkTransition from "../../../transition/components/LinkTransition/LinkTransition";
import { API_BASE } from "../../../constants/config";

export interface CaseItemData {
  id: number;
  slug: string;
  containerClassName?: string;
  backgroundColor: string;
  className?: string;
  images: string[];
  title: string;
  description: string;
}

// Keep backward compat — Cases.tsx still imports this
export interface ProjectItem extends CaseItemData {
  tags: string[];
}

const ANIMATION_DURATION = 0.2;

interface CasesItemProps {
  item: CaseItemData;
  chips: string[];
  href: string;
  titleAs?: "span" | "h2";
  handleHover?: () => void;
  handleLeave?: () => void;
}

export const CasesItem = ({
  item,
  chips,
  href,
  titleAs: Title = "span",
  handleHover,
  handleLeave,
}: CasesItemProps) => {
  const pRef = useRef<HTMLParagraphElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (!pRef.current) return;
      if (window.innerWidth < 1000) {
        gsap.set(pRef.current, { clearProps: "maxHeight,marginBottom" });
      } else {
        gsap.set(pRef.current, { maxHeight: 1, marginBottom: 0 });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onMouseEnter = () => {
    if (!pRef.current || !imgRef.current || window.innerWidth < 1000) return;
    gsap.to(imgRef.current, {
      scale: 1.1,
      duration: ANIMATION_DURATION,
      ease: "power2.inOut",
    });
    gsap.to(pRef.current, {
      maxHeight: pRef.current.scrollHeight,
      marginBottom: 16,
      duration: ANIMATION_DURATION,
      ease: "power2.inOut",
    });
  };

  const onMouseLeave = () => {
    if (!pRef.current || !imgRef.current || window.innerWidth < 1000) return;
    gsap.to(imgRef.current, {
      scale: 1,
      duration: ANIMATION_DURATION,
      ease: "power2.inOut",
    });
    gsap.to(pRef.current, {
      maxHeight: 1,
      marginBottom: 0,
      duration: ANIMATION_DURATION,
      ease: "power2.inOut",
    });
  };

  return (
    <div
      className={styles.case}
      onPointerEnter={handleHover}
      onPointerLeave={handleLeave}
      data-case={JSON.stringify(item)}
      data-active="false"
      data-hover="cases"
    >
      <LinkTransition
        href={href}
        className={`${styles.caseContainer} ${item.containerClassName || ""}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-hover="cases"
      >
        <TuxdiArrow color={item.backgroundColor} className={item.className} />
        <div className={styles.caseContent}>
          <picture>
            <img
              ref={imgRef}
              src={`${API_BASE}${item.images[0]}`}
              alt={item.title || "Case"}
              loading="lazy"
            />
          </picture>
          <div className={styles.caseInfo}>
            <Title>{item.title}</Title>
            <p ref={pRef}>{item.description}</p>
            <ul>
              {chips.map((chip, index) => (
                <li key={index}>
                  {chip}
                  {index !== chips.length - 1 && <small>+</small>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </LinkTransition>
    </div>
  );
};
